import { Text } from '@components/ui';
import type {
  GroupedOptions,
  RatingDamage,
  SelectedEntry,
} from '@features/buyer/jobs/criteria-option/criteriaOptionTypes';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

interface CriteriaSelectionsProps {
  r8tingCriteriaDamageId: Array<string | number>;
  damages?: RatingDamage[];
  hasOptions?: boolean;
  typeOption: GroupedOptions[];
  selectedOptionsArr: SelectedEntry[];
}

const pillListSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0.75,
  listStyle: 'none',
  m: 0,
  p: 0,
};

const pillSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 26,
  px: 1.25,
  borderRadius: 999,
  border: 1,
  borderColor: 'divider',
  fontSize: 13,
  fontWeight: 600,
  color: 'text.primary',
};

const selectedPillSx: SxProps<Theme> = {
  ...pillSx,
  borderColor: 'transparent',
  bgcolor: 'action.selected',
  fontWeight: 700,
};

const sectionLabelSx: SxProps<Theme> = {
  display: 'block',
  mb: 0.75,
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
};

export function CriteriaSelections({
  r8tingCriteriaDamageId,
  damages,
  hasOptions,
  typeOption,
  selectedOptionsArr,
}: CriteriaSelectionsProps): JSX.Element {
  const { t } = useTranslation();
  const criteriaDamages = (damages ?? []).filter((item) =>
    r8tingCriteriaDamageId.includes(item.id)
  );

  return (
    <>
      {criteriaDamages.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <Text as="span" sx={sectionLabelSx}>
            {t('rating.damage')}
          </Text>
          <Box component="ul" sx={pillListSx}>
            {criteriaDamages.map((item) => (
              <Box component="li" key={item.id} sx={pillSx}>
                {item.title}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
      {hasOptions
        ? typeOption.map((item, index) => {
            const selected = item.options.filter((option) =>
              selectedOptionsArr.some((entry) => entry.id === option.id)
            );
            if (selected.length === 0) return null;
            return (
              <Box key={item.heading ?? index} sx={{ mt: 1.5 }}>
                {item.heading ? (
                  <Text as="span" sx={sectionLabelSx}>
                    {item.heading}
                  </Text>
                ) : null}
                <Box component="ul" sx={pillListSx}>
                  {selected.map((option) => (
                    <Box component="li" key={option.id} sx={selectedPillSx}>
                      {String(option.ratingValue ?? '').trim()}
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })
        : null}
    </>
  );
}
